import {
  generateBaseProjection,
  getTradingModifiedProjectionsChangeSet
} from '@bo-utils/trading';
import {
  rankThenNameSort,
  uniqueLeagueDurations
} from '@bo-utils/leagueProjectionFilters';
import { useEffect, useMemo, useState } from 'react';
import type { ActionMenuOptions } from '@library/types/playerModal';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueInfo } from '@library/types/leagues';
import type { PlayerDescriptors } from '@library/types/player';
import { PlayerModal } from '@bo-components/playerModal/PlayerModal';
import type { SelectOptions } from '@library/types/utils';
import { convertLeagueProjectionAttributesToSelectOptions } from '@library/component-utils/filters';
import { getCalculatedBoardStartTimes } from '@bo-utils/leagueTimes';
import { getErrorMessage } from '@bo-utils/common';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { useCopyProjections } from '@bo-hooks/playerModal/useCopyProjections';
import { useGetPlayerModalProjectionsQuery } from '@bo-services/playerModalApi';
import { useImmer } from 'use-immer';
import { useLeagueDurationAttributes } from '@bo-hooks/useLeagueDurationAttributes';
import { useTradingActions } from '@bo-hooks/trading/useTradingActions';

export interface PlayerProjectionsModalProps {
  boardTime: string;
  description: string;
  durationId: string;
  foreignId: string;
  gameId: string;
  gameStartTime: string | undefined;
  isOpen: boolean;
  onCancelModal: () => void;
  leagueInfo: LeagueInfo | undefined;
  playerDescriptors: PlayerDescriptors;
  playerId: string;
  position?: string;
  startTime: string;
  team?: string;
  teamName?: string | null;
  testId?: string;
  isDisplayTimeVisible?: boolean;
}

export const PlayerProjectionsModal = ({
  boardTime,
  description,
  durationId,
  foreignId,
  gameId,
  gameStartTime,
  isOpen,
  onCancelModal,
  leagueInfo,
  playerId,
  playerDescriptors,
  position = '',
  startTime,
  team = '',
  teamName = undefined,
  testId = 'player-projections-modal',
  isDisplayTimeVisible = false
}: PlayerProjectionsModalProps) => {
  const [selectedDuration, setSelectedDuration] = useState(durationId);

  const {
    leagueId,
    name: leagueName,
    statTypes
  } = useLeagueDurationAttributes({
    leagueInfo,
    durationId: selectedDuration
  });

  const allDurations = useMemo(
    () => uniqueLeagueDurations(leagueInfo),
    [leagueInfo]
  );

  const fullDuration = allDurations?.find(
    (duration) => duration.leagueProjectionFilterName === 'Full'
  );

  const nonFullDurations = rankThenNameSort({
    leagueProjectionAttributesList:
      allDurations?.filter(
        (duration) => duration.leagueProjectionFilterName !== 'Full'
      ) ?? []
  });

  const durations = fullDuration
    ? [fullDuration, ...nonFullDurations]
    : nonFullDurations;

  const isSameAsGlobalDurationId = durationId === selectedDuration;

  const { currentData: playerModalProjectionsData, isFetching } =
    useGetPlayerModalProjectionsQuery(
      {
        durationId: hasStandAloneOwnAttributes({ name: leagueInfo?.name })
          ? selectedDuration
          : '',
        fromDate: startTime,
        foreignId,
        gameId,
        leagueId,
        playerId,
        search: playerDescriptors.playerName
      },
      { skip: !isOpen, refetchOnMountOrArgChange: true }
    );

  const {
    handlePublishProjections,
    isPublishProjectionsError,
    isPublishProjectionsSuccess,
    isPublishProjectionsUninitialized,
    publishProjectionsError,
    resetCreateProjections
  } = useTradingActions();

  const { filteredCopiedProjections, isFullDuration, handleCopyProjections } =
    useCopyProjections({
      fetchedProjections: playerModalProjectionsData ?? [],
      durations: allDurations,
      gameStartTime,
      playerDescriptors,
      selectedLeagueName: leagueName,
      selectedDuration,
      statTypes
    });

  const [newProjections, setNewProjections] = useImmer<
    Array<GamePlayerProjectionTrading>
  >(filteredCopiedProjections);

  useEffect(() => {
    if (
      !isFullDuration &&
      !isFetching &&
      newProjections.length === 0 &&
      filteredCopiedProjections.length > 0
    ) {
      setNewProjections(filteredCopiedProjections);
    }
  }, [
    filteredCopiedProjections,
    isFetching,
    isFullDuration,
    newProjections,
    setNewProjections
  ]);

  const [changes, setChanges] = useImmer<
    Array<Partial<GamePlayerProjectionTrading>>
  >([]);

  const updateProjection = ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => {
    const { projectionId } = initialProjection;

    setChanges((draft) => {
      const affectedProjectionIndex = draft.findIndex(
        (projection) => projection.projectionId === projectionId
      );

      if (affectedProjectionIndex === -1) {
        draft.push({
          projectionId,
          ...change
        });

        return;
      }

      draft[affectedProjectionIndex] = {
        ...draft[affectedProjectionIndex],
        ...change
      };
    });
  };

  const updateNewProjection = ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => {
    const { projectionId } = initialProjection;

    setNewProjections((draft) => {
      const affectedProjectionIndex = draft.findIndex(
        (projection) => projection.projectionId === projectionId
      );

      if (affectedProjectionIndex === -1) {
        draft.push({
          ...initialProjection,
          ...change
        });

        return;
      }

      draft[affectedProjectionIndex] = {
        ...draft[affectedProjectionIndex],
        ...change
      };
    });
  };

  const handleResetProjections = () => {
    setChanges([]);
    setNewProjections([]);
  };

  const handleResetCreateProjections = () => {
    if (!isPublishProjectionsUninitialized) {
      resetCreateProjections();
    }
  };

  const handleDurationChange = (selectedDurationId: string) => {
    handleResetCreateProjections();
    handleResetProjections();
    setSelectedDuration(selectedDurationId);
  };

  const statOptions: SelectOptions =
    convertLeagueProjectionAttributesToSelectOptions(statTypes ?? []);

  const durationOptions: SelectOptions =
    convertLeagueProjectionAttributesToSelectOptions(durations ?? []);

  const handleAddProjection = () => {
    setNewProjections((draft) => {
      const initialStartTime = playerModalProjectionsData?.[0]?.startTime;
      const initialBoardTime = playerModalProjectionsData?.[0]?.boardTime;
      const initialDisplayTime =
        playerModalProjectionsData?.[0]?.displayTime ?? '';

      const { boardTime: calculatedBoardTime, startTime: calculatedStartTime } =
        getCalculatedBoardStartTimes({
          durationId,
          durations,
          gameStartTime,
          leagueName
        });

      const starterProjection = generateBaseProjection({
        boardTime: isSameAsGlobalDurationId
          ? boardTime
          : (initialBoardTime ?? calculatedBoardTime),
        dataSource: 'manual',
        description,
        durationId,
        foreignId,
        gameId,
        startTime: initialStartTime ?? calculatedStartTime,
        displayTime: initialDisplayTime,
        playerDescriptors,
        playerId: isSameAsGlobalDurationId ? playerId : '',
        position,
        statTypes,
        team,
        teamName
      });

      draft.push(starterProjection);
    });
  };

  const updatedProjectionList = getTradingModifiedProjectionsChangeSet({
    initialProjections: playerModalProjectionsData ?? [],
    updatedProjections: [...changes, ...newProjections]
  });

  const totalChanges = updatedProjectionList.length;
  const disableActions = !gameStartTime || totalChanges > 0; // This happens when there are projections with a gameId that's not part of the current games list

  const handleCopyProjectionsAction = () => {
    handleCopyProjections({ projections: playerModalProjectionsData ?? [] });
  };

  const ActionsMenuOptions: ActionMenuOptions = [
    {
      disabled: !isFullDuration,
      key: 'COPY_PROJECTIONS',
      name: 'Copy Projections',
      onClick: handleCopyProjectionsAction
    }
  ];

  const errorMessage = isPublishProjectionsError
    ? getErrorMessage({
        isPublishProjectionsError,
        publishProjectionsError
      })
    : '';

  const publishProjections = () => {
    handlePublishProjections(leagueId, updatedProjectionList).catch(() => {});
    setChanges([]);
    setNewProjections([]);
  };

  return (
    <PlayerModal
      changes={changes}
      disableActions={disableActions}
      actionMenuOptions={ActionsMenuOptions}
      durationId={selectedDuration}
      durations={durationOptions}
      errorMessage={errorMessage}
      handleAddProposedProjection={handleAddProjection}
      handlePublishProjections={publishProjections}
      handleResetProjections={handleResetProjections}
      isPublishProjectionsError={isPublishProjectionsError}
      isPublishProjectionsSuccess={isPublishProjectionsSuccess}
      isFetching={isFetching}
      isOpen={isOpen}
      newProjections={newProjections}
      onCancelModal={onCancelModal}
      onDurationChange={handleDurationChange}
      onResetCreateProjections={handleResetCreateProjections}
      playerDescriptors={playerDescriptors}
      playerModalProjectionsData={playerModalProjectionsData ?? []}
      updateNewProjection={updateNewProjection}
      updateProjection={updateProjection}
      statTypes={statOptions}
      testId={testId}
      totalChanges={totalChanges}
      isDisplayTimeVisible={isDisplayTimeVisible}
    />
  );
};
